<!-- eslint-disable no-underscore-dangle -->
<template>
  <BoxWithTitle>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <template #header>
      <v-toolbar-title>
        {{ $t('certificate.pageName') }} {{ networkName }}
      </v-toolbar-title>
    </template>
    <template #content>
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <p class="titleModal">
            {{ $t('certificate.add') }}
          </p>
          <VFile
            v-model="newCertificate.certificate"
            :label="$t('forms.labels.certificate')"
            :rules="rules"
            :accept="mimes"
            :disabled="!hasPermission('certificates.add') || !hasPermission('certificates.edit') || !hasPermission('files.view')"
            :isEdition="!isNew"
            :showSize="false"
          />
        </v-col>
      </v-row>
      <v-row
        align="center"
        justify="end"
      >
        <v-btn
          :disabled="!newCertificate.certificate || loading"
          type="submit"
          color="secondary"
          @click.prevent="onClickSave"
        >
          {{ $t('forms.btn.save') }}
        </v-btn>
      </v-row>
      <v-row>
        <v-col>
          <TableRemote
            v-if="hasPermission('certificates.edit')"
            hideDefaultFooter
            :headers="tableHeaders"
            :items="certificateHistory"
            :itemActions="actions"
            :sortableColumns="[]"
            :pagination="{}"
            :disablePagination="true"
            :isSearchable="false"
            @iconAction="onClickDeleteCertificate"
          >
            <template #item.startDate="{item}">
              <v-col
                md="6"
                sm="12"
                xs="12"
                cols="12"
                class="blocDate"
              >
                <div class="blocDatePicker">
                  <VDatePicker
                    v-model="item.startDate"
                    dense
                    :label="$t('forms.labels.startDate')"
                    :locale="locale"
                    data-cy="CertificateStartDate"
                    @input="(value) => editDate(item, value, 'startDate')"
                  />
                </div>
              </v-col>
            </template>
            <template #item.endDate="{item}">
              <v-col
                md="6"
                sm="12"
                xs="12"
                cols="12"
                class="blocDate"
              >
                <div class="blocDatePicker">
                  <VDatePicker
                    v-model="item.endDate"
                    dense
                    :label="$t('forms.labels.startDate')"
                    :locale="locale"
                    data-cy="CertificateEndDate"
                    @input="(value) => editDate(item, value, 'endDate')"
                  />
                </div>
              </v-col>
            </template>
          </TableRemote>
        </v-col>
      </v-row>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VFile from '@/components/shared/widgets/forms/VuetifyInputs/VFile';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import _datePickerLocales from '@/locales/date-picker-locales';
import VDatePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDatePicker';
import moment from 'moment';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';
import RedirectModal from '@/components/shared/RedirectModal.vue';

export default {
    components: {
        TableRemote,
        BoxWithTitle,
        VFile,
        VDatePicker,
        RedirectModal,
    },
    data() {
        return {
            certificateHistory: [],
            certificate: null,
            newCertificate: {
            },
            showRedirectModal: false,
            localComponentId: null,
            actionName: 'certificate',
            dateNewCertificate: {
                startDate: '',
                endDate: '',
            },
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'network',
            'component',
            'hasPermission',
            'site',
        ]),
        mimes() {
            return ['application/pdf'];
        },
        rules() {
            return {
                size: 10000,
                mimes: this.mimes,
            };
        },
        isNew() {
            return isEmpty(this.certificate);
        },
        networkName() {
            const networkName = `${this.network.name} (${this.network.type.key})`;
            if (!isEmpty(this.component)) {
                return `${networkName} -> ${this.component.name} (${this.component.type.key})`;
            }

            return networkName;
        },
        tableHeaders() {
            return [
                'name',
                'startDate',
                'endDate',
                'createdAt',
            ].map(header => ({
                text: this.$t(`certificate.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('certificates.remove')) {
                actions.push('delete');
            }

            return actions;
        },
        locale() {
            return _datePickerLocales[this.lang];
        },
    },

    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },

    async created() {
        if (this.network.id) {
            const params = isEmpty(this.component) ? {} : { componentId: this.component.id };
            const { data } = await this.$rq.getCertificates(this.network.id, params);
            [this.certificate] = data;
            await this.getCertificateHistory();
        }
        this.localComponentId = this.$route.params.componentId || this.component?.id;
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.localComponentId) {
            lockService.releaseLock(this.localComponentId, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.localComponentId) {
                const releaseStatus = await lockService.releaseLock(this.localComponentId, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    methods: {
        async getCertificateHistory() {
            const params = isEmpty(this.component) ? {} : { componentId: this.component.id };
            const { data } = await this.$rq.getCertificatesHistory(this.network.id, params);
            this.certificateHistory = data.map(certificate => ({
                ...certificate,
                name: certificate.certificate.fileName,
                createdAt: this.$options.filters.formatDate(certificate.createdAt, 'L LT'),
                startDate: certificate.startDate || null,
                endDate: certificate.endDate || null,
            }));
        },
        handleUnload() {
            if (this.localComponentId) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.localComponentId, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.localComponentId, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.localComponentId, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        async onClickSave(file) {
            if (file) {
                this.addCertificate();
                this.newCertificate.certificate = null;
            }
        },
        preparePayload() {
            const formData = new FormData();
            formData.append('certificate', this.newCertificate.certificate);

            if (!isEmpty(this.component)) {
                formData.append('componentId', this.component.id);
            }
            return formData;
        },
        async addCertificate() {
            this.loading = true;
            const payload = this.preparePayload();
            const { data } = await this.$rq.addCertificate(this.network.id, payload);

            this.$toasted.global.toast_success({
                message: this.$t('certificate.added'),
            });
            /* Add the startDate and endDate */
            if (this.dateNewCertificate.startDate || this.dateNewCertificate.endDate) {
                const bodyParams = {};
                // eslint-disable-next-line max-len
                if (this.dateNewCertificate.startDate) { bodyParams.startDate = moment(this.dateNewCertificate.startDate).tz(this.site.tz).toISOString(); }
                if (this.dateNewCertificate.endDate) bodyParams.endDate = moment(this.dateNewCertificate.endDate).tz(this.site.tz).toISOString();
                this.newCertificate = {};
                this.dateNewCertificate = {
                    startDate: '',
                    endDate: '',
                };
                // eslint-disable-next-line no-underscore-dangle
                await this.$rq.editCertificate(data.network._id, data.id, bodyParams);
            }
            this.getCertificateHistory();
            this.loading = false;
        },
        async editDate(item, value, typeDate) {
            const bodyParams = {};
            if (typeDate === 'endDate') bodyParams.endDate = moment(value).tz('utc').toISOString();
            if (typeDate === 'startDate') bodyParams.startDate = moment(value).tz('utc').toISOString();

            await this.$rq.editCertificate(item.network, item.id, bodyParams);
            this.getCertificateHistory();
        },
        onClickDeleteCertificate(certificate) {
            if (!this.hasPermission('files.remove') || !this.hasPermission('certificates.remove')) {
                return;
            }

            this.$root.$emit('showModal', {
                title: this.$t('certificate.delete'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('certificate.confirmRemove') },
                onConfirm: () => this.deleteCertificate(certificate),
                isForm: false,
            });
        },
        async deleteCertificate(certificate) {
            await this.$rq.deleteCertificate(this.network.id, certificate.id);

            this.$toasted.global.toast_success({
                message: this.$t('certificate.deleted'),
            });
            this.getCertificateHistory();
        },
        async onClickOpen({ id, fileName }) {
            if (this.hasPermission('files.view')) {
                const { data } = await this.$rq.downloadFile(id);
                this.$utils.saveFileOnDisk(data, fileName);
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.blocDate {
    display: flex;
    flex-direction: column;
    align-content: center;
    padding-bottom: 0px;
    padding-left: -30px;
  }
.blocDatePicker {
  padding-top: 20px; // 20px is the middle of the height
  min-width: 150px; // For adjust mobile responsive
}
</style>
